/* global process */
/**
 * Cross-app URLs between tenant frontend and System Admin frontend-system.
 * Source of truth: packages/shared-web/src/crossAppUrls.js (vendored into each CRA app).
 */

export const getTenantFrontendUrl = () =>
	(
		process.env.REACT_APP_TENANT_FRONTEND_URL ||
		process.env.REACT_APP_FRONTEND_URL ||
		''
	).replace(/\/$/, '');

export const getSystemFrontendUrl = () =>
	(process.env.REACT_APP_SYSTEM_FRONTEND_URL || '').replace(/\/$/, '');

/** True when this build is the System Admin app. */
export const isSystemAdminApp = () => process.env.REACT_APP_APP_MODE === 'system';

/** Strip every `token` query param from a path or path+search string. */
export const stripTokenFromPath = (pathWithOptionalSearch = '/') => {
	const raw = String(pathWithOptionalSearch || '/');
	const withSlash = raw.startsWith('/') ? raw : `/${raw}`;
	const qIndex = withSlash.indexOf('?');
	if (qIndex === -1) return withSlash || '/';

	const pathname = withSlash.slice(0, qIndex) || '/';
	const params = new URLSearchParams(withSlash.slice(qIndex + 1));
	params.delete('token');
	const remaining = params.toString();
	return remaining ? `${pathname}?${remaining}` : pathname;
};

const isCurrentOrigin = (absoluteBaseUrl) => {
	if (typeof window === 'undefined' || !absoluteBaseUrl) return false;
	try {
		return new URL(absoluteBaseUrl).origin === window.location.origin;
	} catch {
		return false;
	}
};

/** Set a single `token` query param on an absolute URL (replaces any existing). */
export const withAuthToken = (absoluteUrl, token = null) => {
	if (!absoluteUrl) return null;
	if (!token) return absoluteUrl;
	try {
		const url = new URL(absoluteUrl);
		url.searchParams.delete('token');
		url.searchParams.set('token', token);
		return url.toString();
	} catch {
		const cleaned = stripTokenFromPath(absoluteUrl);
		const separator = cleaned.includes('?') ? '&' : '?';
		return `${cleaned}${separator}token=${encodeURIComponent(token)}`;
	}
};

/**
 * Build an absolute URL on the other app, optionally appending session token once.
 * Never stacks duplicate `token` params (avoids nginx 414 on redirect loops).
 */
export const buildCrossAppUrl = (baseUrl, pathname = '/', token = null) => {
	if (!baseUrl) return null;
	const base = String(baseUrl).replace(/\/$/, '');
	const cleanPath = stripTokenFromPath(
		pathname.startsWith('/') ? pathname : `/${pathname}`,
	);
	return withAuthToken(`${base}${cleanPath}`, token);
};

/** Where a System Admin should land after auth (system app dashboard). */
export const getSystemAdminAppHomePath = () => '/dashboard/system';

/**
 * If the current app is wrong for this role, return a redirect URL; else null.
 */
export const resolveCrossAppRoleRedirectUrl = ({
	userRole,
	token,
	pathname = '/',
	search = '',
} = {}) => {
	if (!userRole) return null;

	const systemBase = getSystemFrontendUrl();
	const tenantBase = getTenantFrontendUrl();
	const onSystemApp = isSystemAdminApp();

	if (userRole === 'System Admin') {
		if (onSystemApp) return null;
		if (!systemBase) return null;
		// Already on the system-admin host (wrong APP_MODE / build) — do not loop.
		if (isCurrentOrigin(systemBase)) return null;
		const targetPath =
			pathname === '/' || pathname === '/auth' || pathname === '/signup'
				? getSystemAdminAppHomePath()
				: stripTokenFromPath(`${pathname}${search || ''}`);
		return buildCrossAppUrl(systemBase, targetPath, token);
	}

	// Non–System Admin on the system app → tenant app
	if (onSystemApp) {
		if (!tenantBase) return null;
		if (isCurrentOrigin(tenantBase)) return null;
		const dashboard =
			userRole === 'Company Admin'
				? '/dashboard/company'
				: userRole === 'Employee'
					? '/dashboard/employee'
					: '/';
		return buildCrossAppUrl(tenantBase, dashboard, token);
	}

	return null;
};
